"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { getCurrencySymbol, type Currencies } from "@drpcrd/utils";
import { cn } from "@drpcrd/utils/shared";
import { Form } from "../form";
import { InputTextField } from "../input-text-field";
import { InputTextArea } from "../input-text-area";
import type { OutreachResponseFooter } from "./types";

const schema = z.object({
  quote: z.number().min(0).optional(),
  comment: z.string().trim().nonempty("Comment is required"),
});

type FormData = z.infer<typeof schema>;

type Props = {
  currency: Currencies;
  /** The quote arrives in pennies, or undefined when none was given. */
  onSubmit: (data: { priceInPennies?: number; comment: string }) => void;
  pending: boolean;
  footer: OutreachResponseFooter;
  /** See `OutreachNotInterestedForm`'s `className`. */
  className?: string;
};

/**
 * "Interested" response to a brand's outreach — an optional quote and a
 * required comment for the brand. Shared by the creator app and the talent
 * agency app; the caller supplies the submit control through `footer`.
 */
export const OutreachInterestedForm = ({
  currency,
  onSubmit,
  pending,
  footer,
  className,
}: Props) => {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: { quote: undefined, comment: "" },
  });

  const { comment } = form.watch();

  const submit = form.handleSubmit((data) =>
    onSubmit({
      priceInPennies: data.quote
        ? Math.round(Number(data.quote) * 100)
        : undefined,
      comment: data.comment,
    }),
  );

  return (
    <>
      <div className={cn("flex w-full flex-col", className)}>
        <Form {...form}>
          <form onSubmit={submit} className="flex w-full flex-col gap-4">
            <InputTextField
              control={form.control}
              name="quote"
              label="Your quote"
              description="Optional, but recommended"
              type="number"
              step="0.01"
              prefix={getCurrencySymbol(currency)}
              placeholder="0.00"
              inputClassName="pl-2!"
            />

            <InputTextArea
              control={form.control}
              name="comment"
              label="Leave a comment"
              placeholder="Something for the brand to know"
            />
          </form>
        </Form>
      </div>

      {footer({ submit, disabled: !comment.trim() || pending, pending })}
    </>
  );
};
